import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const featureList = [
  'about.features.authentic',
  'about.features.nature',
  'about.features.community',
  'about.features.excellence',
];

const aboutShowcaseImages = [
  '/Screenshot 2026-04-07 150059.png',
  '/Screenshot 2026-04-07 150129.png',
  '/Screenshot 2026-04-07 150225.png',
  '/Screenshot 2026-04-07 150308.png',
] as const;

const AboutPage = () => {
  const { t } = useTranslation();

  return (
    <section className="bg-[#fffaf7]">

      {/* ── Hero ── grand library hall */}
      <section
        className="relative overflow-hidden rounded-3xl"
        style={{ backgroundColor: '#7c3d12' }} // fallback if bg-image fails
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('${aboutShowcaseImages[0]}')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-orange-900/80 via-orange-700/70 to-orange-800/85" />
        <div className="relative mx-auto max-w-6xl px-4 py-24 text-center text-white sm:px-6 lg:px-8">
          <img
            src="/Screenshot_2026-02-18_120944-removebg-preview.png"
            alt={t('common.brand')}
            className="mx-auto h-16 w-16 rounded-xl bg-white/90 p-1 object-contain"
          />
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-orange-100">
            {t('about.overline')}
          </p>
          <h1 className="mt-3 text-5xl font-semibold md:text-6xl">
            {t('about.title')}
          </h1>
          <p className="mx-auto mt-4 max-w-3xl text-sm text-orange-50 md:text-base">
            {t('about.subtitle')}
          </p>
          <p className="mt-6 text-sm font-semibold text-orange-100">
            {t('nav.home')} &gt; {t('about.title')}
          </p>
        </div>
      </section>

      {/* ── Section One ── */}
      <section className="mx-auto grid max-w-6xl gap-10 px-4 py-14 sm:px-6 lg:grid-cols-2 lg:px-8">
        <article className="space-y-4">
          <p className="text-xs font-semibold uppercase tracking-[0.15em] text-orange-700">
            {t('about.sectionOneTag')}
          </p>
          <h2 className="text-4xl font-semibold text-slate-900">
            {t('about.sectionOneTitle')}
          </h2>
          <p className="text-sm text-slate-600">
            {t('about.sectionOneDescription')}
          </p>
          <div className="grid grid-cols-2 gap-3">

            {/* Person reading a book */}
            <img
              src={aboutShowcaseImages[0]}
              alt="Born Hutsi cover"
              className="h-36 w-full rounded-xl object-cover sm:h-44 bg-orange-100"
            />

            {/* Headphones — audiobooks */}
            <img
              src={aboutShowcaseImages[1]}
              alt="Tota's Fest cover"
              className="h-36 w-full rounded-xl object-cover sm:h-44 bg-orange-100"
            />

            {/* Colourful books on a shelf */}
            <img
              src={aboutShowcaseImages[2]}
              alt="Myragitwa cover"
              className="col-span-2 h-40 w-full rounded-xl object-cover sm:h-48 bg-orange-100"
            />
          </div>
        </article>

        <article className="rounded-2xl border border-orange-100 bg-white p-6 shadow-sm">
          <h3 className="text-3xl font-semibold text-slate-900">
            {t('about.sectionOneAsideTitle')}
          </h3>
          <p className="mt-3 text-sm text-slate-600">
            {t('about.sectionOneAsideDescription')}
          </p>
          <ul className="mt-5 space-y-3">
            {featureList.map((featureKey) => (
              <li
                key={featureKey}
                className="rounded-xl bg-orange-50 px-4 py-3 text-sm font-medium text-orange-800"
              >
                {t(featureKey)}
              </li>
            ))}
          </ul>
        </article>
      </section>

      {/* ── Banner ── library reading room */}
      <section
        className="relative overflow-hidden"
        style={{ backgroundColor: '#7c3d12' }} // fallback colour
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('${aboutShowcaseImages[2]}')`,
          }}
        />
        <div className="absolute inset-0 bg-orange-900/70" />
        <div className="relative mx-auto max-w-6xl px-4 py-16 text-center text-white sm:px-6 lg:px-8">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-orange-100">
            {t('about.bannerOverline')}
          </p>
          <h3 className="mt-3 text-4xl font-semibold md:text-5xl">
            {t('about.bannerTitle')}
          </h3>
          <p className="mx-auto mt-3 max-w-3xl text-sm text-orange-50">
            {t('about.bannerDescription')}
          </p>
          <Link
            to="/products"
            className="mt-6 inline-flex rounded-full border border-white/65 px-5 py-2 text-sm font-semibold text-white transition hover:bg-white/10"
          >
            {t('about.bannerCta')}
          </Link>
        </div>
      </section>

      {/* ── Section Two ── open book / studying */}
      <section className="mx-auto grid max-w-6xl items-center gap-10 px-4 py-14 sm:px-6 lg:grid-cols-2 lg:px-8">
        <article className="overflow-hidden rounded-2xl border border-orange-100 bg-orange-50 shadow-sm">
          <img
            src={aboutShowcaseImages[3]}
            alt={t('about.sectionTwoTitle')}
            className="h-72 w-full object-cover"
          />
        </article>
        <article>
          <p className="text-xs font-semibold uppercase tracking-[0.15em] text-orange-700">
            {t('about.sectionTwoTag')}
          </p>
          <h3 className="mt-2 text-4xl font-semibold text-slate-900">
            {t('about.sectionTwoTitle')}
          </h3>
          <p className="mt-4 text-sm text-slate-600">
            {t('about.sectionTwoDescription')}
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/products"
              className="rounded-xl bg-orange-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-orange-700"
            >
              {t('about.primaryCta')}
            </Link>
            <Link
              to="/"
              className="rounded-xl border border-orange-200 bg-white px-5 py-3 text-sm font-semibold text-orange-700 transition hover:bg-orange-50"
            >
              {t('about.secondaryCta')}
            </Link>
          </div>
        </article>
      </section>

    </section>
  );
};

export default AboutPage;
